import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, BookOpen, User, Phone, Edit3, AlertCircle, RefreshCw, Share2 } from 'lucide-react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import LoadMoreButton from '../ui/LoadMoreButton';
import { studentService, courseService } from '../../services';
import { PermissionGuard } from '../auth/PermissionGuard';
import { ButtonPermissions } from '../../types/permission';

const AcademicView = ({ academicTab, setAcademicTab, onSearch, onAddItem, onEditStudent, onTokenAccess, onEditCourse, onShare, searchParams = {}, searchTrigger = 0 }) => {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef(null);
  

  // 加载学员列表
  const loadStudents = async (pageNum = 1, append = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // 构建请求参数
      const requestParams = {
        page: pageNum,
        per_page: 5,
        sort_by: 'created_at',
        sort_order: 'desc',
        ...searchParams,
      };
      
      // 如果没有明确指定student_type，则默认查询"在学"状态的学生
      // 空字符串表示用户选择了"全部"，此时不设置默认值
      if (!('student_type' in searchParams)) {
        requestParams.student_type = '在学';
      } else if (searchParams.student_type === '') {
        // 用户选择"全部"，删除空字符串参数让后端返回所有状态
        delete requestParams.student_type;
      }
      
      const response = await studentService.getList(requestParams);

      const items = response.items || [];
      
      if (append) {
        setStudents(prev => [...prev, ...items]);
      } else {
        setStudents(items);
      }
      
      setHasMore(response.has_next || false);
      setPage(pageNum);
    } catch (err) {
      console.error('加载学员失败:', err);
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载课程列表
  const loadCourses = async (pageNum = 1, append = false) => {
    try {
      setLoading(true);
      setError(null);

      // 默认只展示"可用"状态的课程，搜索参数可覆盖
      const courseParams = {
        page: pageNum,
        per_page: 5,
        status: '可用',
        ...searchParams,
      };
      // 搜索时若 status 为空字符串表示"全部"，则删除以不过滤
      if (courseParams.status === '') {
        delete courseParams.status;
      }

      const response = await courseService.getList(courseParams);

      const items = response.items || [];
      
      if (append) {
        setCourses(prev => [...prev, ...items]);
      } else {
        setCourses(items);
      }
      
      setHasMore(response.has_next || false);
      setPage(pageNum);
    } catch (err) {
      console.error('加载课程失败:', err);
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  // 根据 tab 切换加载数据
  useEffect(() => {
    if (academicTab === 'students') {
      loadStudents();
    } else {
      loadCourses();
    }
  }, [academicTab]);

  // 搜索触发时重新加载（包括选择"全部"）
  useEffect(() => {
    if (searchTrigger > 0) {
      if (academicTab === 'students') {
        loadStudents(1);
      } else {
        loadCourses(1);
      }
    }
  }, [searchTrigger]);

  // 加载更多
  const handleLoadMore = () => {
    if (!loading && hasMore) {
      if (academicTab === 'students') {
        loadStudents(page + 1, true);
      } else {
        loadCourses(page + 1, true);
      }
    }
  };

  // 使用 IntersectionObserver 监听滚动到底部自动加载更多
  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !loading && hasMore && !error) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinelRef.current);

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, [hasMore, loading, page, academicTab, error]);

  // 刷新当前列表
  const handleRefresh = () => {
    if (academicTab === 'students') {
      loadStudents(1);
    } else {
      loadCourses(1);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#F7FAFC]">
      {/* Header */}
      <header className="h-[48px] px-4 bg-white flex items-center justify-between border-b border-[#EDF2F7] flex-shrink-0">
        <h1 className="text-base font-bold text-[#2D3748]">教务管理</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="icon"
            icon={RefreshCw}
            onClick={handleRefresh}
            disabled={loading}
            className={loading ? 'animate-spin' : ''}
          />
          <Button variant="icon" icon={Search} onClick={() => onSearch(academicTab === 'students' ? 'student' : 'course')} />
          <PermissionGuard
            permission={academicTab === 'students' ? ButtonPermissions.students.create : ButtonPermissions.courses.create}
          >
            <Button variant="primary" size="small" icon={Plus} onClick={onAddItem}>
              新增
            </Button>
          </PermissionGuard>
        </div>
      </header>

    {/* Tabs Switcher */}
    <div className="bg-white px-4 py-2 border-b border-[#EDF2F7]">
      <div className="flex bg-[#F7FAFC] p-1 rounded-lg">
        <button 
          className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${academicTab === 'students' ? 'bg-white text-[#3182CE] shadow-sm' : 'text-[#718096]'}`}
          onClick={() => setAcademicTab('students')}
        >
          学生管理
        </button>
        <button 
          className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${academicTab === 'courses' ? 'bg-white text-[#3182CE] shadow-sm' : 'text-[#718096]'}`}
          onClick={() => setAcademicTab('courses')}
        >
          课程管理
        </button>
      </div>
    </div>

    {/* Content */}
    <main className="flex-1 overflow-y-auto px-4 pt-4 pb-24 space-y-3">
      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700 font-medium">加载失败</p>
            <p className="text-xs text-red-600 mt-1">{error}</p>
            <button 
              onClick={handleRefresh}
              className="mt-2 text-xs text-red-700 underline"
            >
              重试
            </button>
          </div>
        </div>
      )}

      {/* 加载中 */}
      {loading && (academicTab === 'students' ? students.length === 0 : courses.length === 0) && (
        <div className="flex flex-col items-center justify-center py-12">
          <RefreshCw size={32} className="text-[#3182CE] animate-spin mb-3" />
          <p className="text-sm text-[#718096]">加载中...</p>
        </div>
      )}

      {/* 学生列表 */}
      {academicTab === 'students' ? (
        <>
          {/* 表格容器 */}
          {students.length > 0 && (
            <div className="bg-white rounded-xl overflow-hidden border border-[#EDF2F7]">
              {/* 表头 */}
              <div className="grid grid-cols-[1fr_auto] items-center px-3 py-2 bg-[#F7FAFC] border-b border-[#EDF2F7]">
                <span className="text-[11px] font-semibold text-[#718096] uppercase tracking-wide">学员 / 联系方式</span>
                <span className="text-[11px] font-semibold text-[#718096] uppercase tracking-wide text-right">操作</span>
              </div>
              {/* 表格行 */}
              {students.map((student, idx) => (
                <div
                  key={student.id}
                  className={`grid grid-cols-[1fr_auto] items-center px-3 py-2 gap-2 ${
                    idx !== students.length - 1 ? 'border-b border-[#EDF2F7]' : ''
                  } ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
                >
                  {/* 学员信息 */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="font-semibold text-[13px] text-[#2D3748] truncate max-w-[90px]">{student.name}</span>
                      {student.student_type && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                          student.student_type === '在学'
                            ? 'bg-green-100 text-green-700'
                            : student.student_type === '潜在'
                            ? 'bg-yellow-100 text-yellow-700'
                            : student.student_type === '已结束'
                            ? 'bg-gray-100 text-gray-500'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {student.student_type}
                        </span>
                      )}
                      {student.grade && (
                        <span className="text-[10px] text-[#718096] bg-gray-100 px-1.5 py-0.5 rounded-full flex-shrink-0">{student.grade}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-[#718096]">
                      {(student.gender || student.age) && (
                        <span className="flex items-center gap-0.5">
                          <User size={9} />
                          {student.gender && student.age
                            ? `${student.gender} ${student.age}岁`
                            : student.gender || `${student.age}岁`}
                        </span>
                      )}
                      {student.contact && (
                        <span className="flex items-center gap-0.5 truncate">
                          <Phone size={9} />{student.contact}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* 操作按钮 */}
                  <div className="flex gap-0.5 flex-shrink-0">
                    <button
                      className="p-1.5 text-gray-400 hover:text-[#3182CE] hover:bg-blue-50 rounded-lg transition-colors"
                      onClick={() => onTokenAccess(student)}
                      title="分享给家长"
                    >
                      <Share2 size={14} />
                    </button>
                    <PermissionGuard permission={ButtonPermissions.students.edit}>
                      <button
                        className="p-1.5 text-gray-400 hover:text-[#3182CE] hover:bg-blue-50 rounded-lg transition-colors"
                        onClick={() => onEditStudent(student)}
                      >
                        <Edit3 size={14} />
                      </button>
                    </PermissionGuard>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 学生空状态 */}
          {!loading && !error && students.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <User size={48} className="text-gray-300 mb-3" />
              <p className="text-sm text-[#718096]">暂无学员</p>
              <PermissionGuard permission={ButtonPermissions.students.create}>
                <button
                  onClick={onAddItem}
                  className="mt-3 text-xs text-[#3182CE] underline"
                >
                  添加第一个学员
                </button>
              </PermissionGuard>
            </div>
          )}
        </>
      ) : (
        <>
          {/* 课程列表 */}
          {courses.map((course, idx) => (
            <Card key={course.id} className={`p-3 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
              <div className="flex justify-between items-start mb-1.5">
                <h3 className="font-bold text-[15px] text-[#2D3748] truncate flex-1">{course.name}</h3>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Badge color={course.status === '可用' ? 'green' : 'gray'} className="text-[11px] px-2 py-0.5">
                    {course.status || '未知'}
                  </Badge>
                  <PermissionGuard permission={ButtonPermissions.courses.edit}>
                    <button 
                      className="p-1 text-gray-400 hover:text-[#3182CE] rounded-md transition-colors"
                      onClick={() => onEditCourse(course)}
                    >
                      <Edit3 size={14} />
                    </button>
                  </PermissionGuard>
                </div>
              </div>
              {course.description && (
                <p className="text-[13px] text-[#718096] mb-2 break-words leading-relaxed">{course.description}</p>
              )}
              <div className="flex justify-between items-center text-xs text-[#4A5568] bg-[#F7FAFC]/50 p-2 rounded-md border border-[#EDF2F7]">
                 <span className="truncate">{course.teacher ? `授课: ${course.teacher}` : '未分配老师'}</span>
                  <PermissionGuard permission={ButtonPermissions.courses.edit}>
                    <span className="text-[#3182CE] font-bold cursor-pointer underline flex-shrink-0" onClick={() => onEditCourse(course)}>详情</span>
                  </PermissionGuard>
              </div>
            </Card>
          ))}

          {/* 课程空状态 */}
          {!loading && !error && courses.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <BookOpen size={48} className="text-gray-300 mb-3" />
              <p className="text-sm text-[#718096]">暂无课程</p>
              <PermissionGuard permission={ButtonPermissions.courses.create}>
                <button 
                  onClick={onAddItem}
                  className="mt-3 text-xs text-[#3182CE] underline"
                >
                  添加第一个课程
                </button>
              </PermissionGuard>
            </div>
          )}
        </>
      )}

      {/* 加载更多哨兵 */}
      <div ref={sentinelRef} className="h-4 w-full" />

      {/* 加载分页 */}
      <LoadMoreButton 
        onClick={handleLoadMore} 
        loading={loading} 
        hasMore={hasMore && (academicTab === 'students' ? students.length > 0 : courses.length > 0)} 
      />
    </main>
  </div>
);
};

export default AcademicView;
